import { Radii } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

export const QueueControlButtonStyleValues = {
  menuWidth: '17.5rem',
}

export enum QueueControlButtonVariant {
  InQueue = 'InQueue',
  OutOfQueue = 'OutOfQueue',
  DoNotDisturb = 'DoNotDisturb',
  CustomOnline = 'CustomOnline',
  CustomAway = 'CustomAway',
}

const QueueControlButtonParts = [
  'menuHeader',
  'menuHeaderText',
  'button',
  'buttonContentsContainer',
  'textContainer',
  'label',
  'secondaryLabel',
  'chevronIcon',
] as const

const helpers = createMultiStyleConfigHelpers(QueueControlButtonParts)
export const QueueControlButtonStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    menuHeader: {
      width: 'full',
      paddingX: '0.5rem',
      paddingTop: '0.5rem',
    },
    menuHeaderText: {
      textStyle: 'Body2_Bold',
    },
    button: {
      width: '11rem',
      height: '2.5rem',
      paddingLeft: '1.25rem',
      paddingRight: '1rem',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: '0',
      borderRadius: Radii.sm,
      cursor: 'pointer',
    },
    buttonContentsContainer: {
      alignItems: 'center',
      gap: '.5rem',
    },
    textContainer: {
      flexDirection: 'column',
    },
    label: {
      lineHeight: '0.875rem',
      maxWidth: '6.25rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontWeight: '600',
    },
    secondaryLabel: {
      ...textStyles[textStyleKeys.Body3],
      lineHeight: '0.875rem',
    },
    chevronIcon: {
      color: 'fg.muted',
      boxSize: '0.75rem',
      '& path': { strokeWidth: '2px' },
    },
  },
  variants: {
    [QueueControlButtonVariant.InQueue]: {
      button: {
        backgroundColor: asChakraVariable('green.solid'),
        _disabled: {
          color: asChakraVariable('fg.muted'),
        },
        _hover: {
          backgroundColor: asChakraVariable('green.emphasized'),
        },
      },
      label: {
        ...textStyles[textStyleKeys.Body2],
        color: asChakraVariable('green.contrast'),
      },
      secondaryLabel: {
        color: asChakraVariable('green.contrast'),
      },
      chevronIcon: {
        color: asChakraVariable('green.contrast'),
      },
    },
    [QueueControlButtonVariant.OutOfQueue]: {
      button: {
        backgroundColor: asChakraVariable('bg.muted'),
        _hover: {
          backgroundColor: asChakraVariable('bg.muted'),
        },
      },
      label: {
        ...textStyles[textStyleKeys.Body2],
        color: asChakraVariable('fg.default'),
        fontWeight: '400',
      },
      secondaryLabel: {
        color: asChakraVariable('fg.default'),
      },
      chevronIcon: {
        color: asChakraVariable('fg.default'),
      },
    },
    [QueueControlButtonVariant.DoNotDisturb]: {
      button: {
        backgroundColor: asChakraVariable('red.solid'),
        _hover: {
          backgroundColor: asChakraVariable('red.emphasized'),
        },
      },
      label: {
        ...textStyles[textStyleKeys.Body2_Semibold],
        color: asChakraVariable('red.contrast'),
      },
      secondaryLabel: {
        color: asChakraVariable('red.contrast'),
      },
      chevronIcon: {
        color: asChakraVariable('red.contrast'),
      },
    },
    [QueueControlButtonVariant.CustomOnline]: {
      button: {
        backgroundColor: asChakraVariable('bg.emphasized'),
        _hover: {
          backgroundColor: asChakraVariable('bg.muted'),
        },
      },
      label: {
        ...textStyles[textStyleKeys.Body2_Semibold],
        color: 'fg.default',
      },
      secondaryLabel: {
        color: 'fg.default',
      },
      chevronIcon: {
        color: 'fg.default',
      },
    },
    [QueueControlButtonVariant.CustomAway]: {
      button: {
        backgroundColor: asChakraVariable('red.solid'),
        _hover: {
          backgroundColor: asChakraVariable('red.emphasized'),
        },
      },
      label: {
        ...textStyles[textStyleKeys.Body2_Semibold],
        color: asChakraVariable('red.contrast'),
      },
      secondaryLabel: {
        color: asChakraVariable('red.contrast'),
      },
      chevronIcon: {
        color: asChakraVariable('red.contrast'),
      },
    },
  },
})

export const useQueueControlButtonStyle = (props: StyleThemingProps) => {
  return mergeMultiStyleConfig(
    QueueControlButtonStyle,
    props,
  ) as MultiStyleType<typeof QueueControlButtonParts>
}
